import { lazy, Suspense, useState } from 'react';
import { FileText, Trash2, Video } from 'lucide-react';
import {
  SLIDE_CALLOUT_TONES,
  SLIDE_IMAGE_SIZES,
  SLIDE_IMAGE_ALIGNS,
  type SlideCalloutTone,
  type SlideLeafBlock,
} from '@hcc/types';
import { ApiError } from '../../../../lib/api';
import { uploadFormFile, uploadFormVideo } from '../../../../lib/upload';
import { SHARED_INPUT_CLASS } from '../pageMeta';
import { Button } from '../../../ui/Button';
import { ImageUploadInput } from '../ImageUploadInput';
import { TableBlockEditor } from './TableBlockEditor';

// Lazy so ProseMirror/TipTap only loads when a text block is actually edited.
const RichTextEditor = lazy(() => import('../RichTextEditor'));

const TONE_LABEL: Record<SlideCalloutTone, string> = {
  info: 'Info (blue)',
  tip: 'Tip (accent)',
  success: 'Success (green)',
  warning: 'Warning (amber)',
  danger: 'Important (red)',
};
const SIZE_LABEL = { small: 'Small', medium: 'Medium', large: 'Large', full: 'Full width' };
const ALIGN_LABEL = { left: 'Left', center: 'Centre', right: 'Right' };

/** Inline editor for one leaf block; dispatches on the block type. */
export function LeafBlockEditor({
  block,
  onChange,
}: {
  block: SlideLeafBlock;
  onChange: (next: SlideLeafBlock) => void;
}) {
  switch (block.type) {
    case 'heading':
    case 'statement':
      return (
        <input
          value={block.text}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder={
            block.type === 'heading' ? 'Heading text' : 'The key message, big and centred'
          }
          className={`${SHARED_INPUT_CLASS} mt-0`}
        />
      );
    case 'text':
      return (
        <Suspense
          fallback={
            <div className="min-h-24 rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg-dim">
              Loading editor…
            </div>
          }
        >
          <RichTextEditor
            value={block.text}
            onChange={(text) => onChange({ ...block, text })}
            placeholder="Paragraph text"
          />
        </Suspense>
      );
    case 'list':
      return (
        <div className="space-y-2">
          <select
            value={block.style}
            onChange={(e) =>
              onChange({ ...block, style: e.target.value as 'bullets' | 'numbered' })
            }
            className={`${SHARED_INPUT_CLASS} mt-0 w-auto`}
          >
            <option value="bullets">Bullet points</option>
            <option value="numbered">Numbered</option>
          </select>
          <textarea
            rows={4}
            value={block.items.join('\n')}
            onChange={(e) => onChange({ ...block, items: e.target.value.split('\n') })}
            placeholder={'One item per line'}
            className={`${SHARED_INPUT_CLASS} mt-0`}
          />
        </div>
      );
    case 'callout':
      return (
        <div className="space-y-2">
          <select
            value={block.tone}
            onChange={(e) => onChange({ ...block, tone: e.target.value as typeof block.tone })}
            className={`${SHARED_INPUT_CLASS} mt-0 w-auto`}
          >
            {SLIDE_CALLOUT_TONES.map((t) => (
              <option key={t} value={t}>
                {TONE_LABEL[t]}
              </option>
            ))}
          </select>
          <textarea
            rows={2}
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="The note to highlight"
            className={`${SHARED_INPUT_CLASS} mt-0`}
          />
        </div>
      );
    case 'image':
      return (
        <div className="space-y-2">
          <ImageUploadInput
            label=""
            value={block.image_key || undefined}
            onChange={(url) => onChange({ ...block, image_key: url ?? '' })}
          />
          {block.image_key ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="font-medium text-fg-muted">Size</span>
                <select
                  value={block.size ?? 'medium'}
                  onChange={(e) =>
                    onChange({ ...block, size: e.target.value as typeof block.size })
                  }
                  className={`${SHARED_INPUT_CLASS} w-auto`}
                >
                  {[...SLIDE_IMAGE_SIZES, 'full' as const].map((s) => (
                    <option key={s} value={s}>
                      {SIZE_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="font-medium text-fg-muted">Alignment</span>
                <select
                  value={block.align ?? 'left'}
                  onChange={(e) =>
                    onChange({ ...block, align: e.target.value as typeof block.align })
                  }
                  className={`${SHARED_INPUT_CLASS} w-auto`}
                >
                  {SLIDE_IMAGE_ALIGNS.map((a) => (
                    <option key={a} value={a}>
                      {ALIGN_LABEL[a]}
                    </option>
                  ))}
                </select>
              </label>
              <input
                value={block.caption ?? ''}
                onChange={(e) => onChange({ ...block, caption: e.target.value || undefined })}
                placeholder="Caption (optional)"
                className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
              />
              <input
                value={block.alt ?? ''}
                onChange={(e) => onChange({ ...block, alt: e.target.value || undefined })}
                placeholder="Describe for screen readers"
                className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
              />
            </div>
          ) : null}
        </div>
      );
    case 'quote':
      return (
        <div className="space-y-2">
          <textarea
            rows={2}
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="The quotation"
            className={`${SHARED_INPUT_CLASS} mt-0`}
          />
          <input
            value={block.attribution ?? ''}
            onChange={(e) => onChange({ ...block, attribution: e.target.value || undefined })}
            placeholder="Who said it (optional)"
            className={`${SHARED_INPUT_CLASS} mt-0`}
          />
        </div>
      );
    case 'pdf':
      return <PdfBlockEditor block={block} onChange={onChange} />;
    case 'video':
      return <VideoBlockEditor block={block} onChange={onChange} />;
    case 'table':
      return <TableBlockEditor block={block} onChange={onChange} />;
    case 'divider':
      return <hr className="border-line/10" />;
  }
}

/** Upload + caption editor for a PDF block. */
function PdfBlockEditor({
  block,
  onChange,
}: {
  block: Extract<SlideLeafBlock, { type: 'pdf' }>;
  onChange: (next: SlideLeafBlock) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      onChange({ ...block, file_key: await uploadFormFile(file) });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        {block.file_key ? (
          <span className="inline-flex items-center gap-1.5 text-fg-muted">
            <FileText className="h-4 w-4" />
            PDF attached
          </span>
        ) : null}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg hover:bg-surface-2">
          <FileText className="h-4 w-4" />
          {uploading ? 'Uploading…' : block.file_key ? 'Replace PDF' : 'Upload PDF'}
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => void onPick(e)}
            disabled={uploading}
          />
        </label>
        {block.file_key ? (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Trash2 className="h-4 w-4" />}
            onClick={() => onChange({ ...block, file_key: '' })}
          >
            Remove
          </Button>
        ) : null}
      </div>
      <input
        value={block.caption ?? ''}
        onChange={(e) => onChange({ ...block, caption: e.target.value || undefined })}
        placeholder="Caption (optional)"
        className={`${SHARED_INPUT_CLASS} mt-0`}
      />
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}

/** Upload + caption editor for a video block (direct-to-S3, with progress). */
function VideoBlockEditor({
  block,
  onChange,
}: {
  block: Extract<SlideLeafBlock, { type: 'video' }>;
  onChange: (next: SlideLeafBlock) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setProgress(0);
    setUploading(true);
    try {
      const url = await uploadFormVideo(file, setProgress);
      onChange({ ...block, file_key: url });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        {block.file_key ? (
          <span className="inline-flex items-center gap-1.5 text-fg-muted">
            <Video className="h-4 w-4" />
            Video attached
          </span>
        ) : null}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg hover:bg-surface-2">
          <Video className="h-4 w-4" />
          {uploading
            ? `Uploading… ${Math.round(progress * 100)}%`
            : block.file_key
              ? 'Replace video'
              : 'Upload video'}
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
            className="hidden"
            onChange={(e) => void onPick(e)}
            disabled={uploading}
          />
        </label>
        {block.file_key ? (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Trash2 className="h-4 w-4" />}
            onClick={() => onChange({ ...block, file_key: '' })}
          >
            Remove
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-fg-dim">
        Upload a web-ready mp4 or webm (max 200MB). Learners must watch it before
        the slide unlocks.
      </p>
      <input
        value={block.caption ?? ''}
        onChange={(e) => onChange({ ...block, caption: e.target.value || undefined })}
        placeholder="Caption (optional)"
        className={`${SHARED_INPUT_CLASS} mt-0`}
      />
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
